/* <size-finder>: a step-by-step size quiz.
   Steps: 1 product, 2 height, 3 weight, 4 shoulders (hoodie only), 5 fit, 6 result.
   The answers and the result are kept in localStorage, so a returning visitor sees the last result and can change it. */
const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL'];
const LIMITS = { height: [140, 220], weight: [40, 150] };

const make = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

class SizeFinder extends HTMLElement {
  connectedCallback() {
    this.steps = [...this.querySelectorAll('[data-step]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.heightInput = this.querySelector('[data-height]');
    this.weightInput = this.querySelector('[data-weight]');
    this.shoulderInputs = [...this.querySelectorAll('[data-shoulder]')];
    this.fitInputs = [...this.querySelectorAll('[data-fit]')];
    this.stepLabel = this.querySelector('[data-step-label]');
    this.abort = new AbortController();
    this.data = this.emptyData();
    this.current = 1;

    this.bind();
    this.restore();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.productsRequest?.abort();
  }

  emptyData() {
    return { product: null, height: null, weight: null, shoulder: 'normal', fit: 'regular', size: null };
  }

  /* ----- events ----- */
  bind() {
    const { signal } = this.abort;

    this.querySelectorAll('[data-product]').forEach((button) => {
      button.addEventListener('click', () => {
        this.data.product = button.dataset.product;
        this.save();
        this.go(2);
      }, { signal });
    });

    const watchNumber = (input, key) => input.addEventListener('input', () => {
      const [min, max] = LIMITS[key];
      const value = parseInt(input.value, 10);
      const valid = value >= min && value <= max;
      input.closest('[data-step]').querySelector('[data-continue]').disabled = !valid;
      this.data[key] = valid ? value : null;
    }, { signal });
    watchNumber(this.heightInput, 'height');
    watchNumber(this.weightInput, 'weight');

    // Enter in a number field behaves like the Continue button.
    [this.heightInput, this.weightInput].forEach((input) => input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      const button = input.closest('[data-step]').querySelector('[data-continue]');
      if (!button.disabled) button.click();
    }, { signal }));

    this.shoulderInputs.forEach((input) => input.addEventListener('change', () => { this.data.shoulder = input.value; this.save(); }, { signal }));
    this.fitInputs.forEach((input) => input.addEventListener('change', () => { this.data.fit = input.value; this.save(); }, { signal }));

    this.querySelectorAll('[data-continue]').forEach((button) => button.addEventListener('click', () => {
      if (this.current === 5) { this.showResult(); return; }
      this.save();
      this.go(this.nextStep());
    }, { signal }));

    this.querySelectorAll('[data-back]').forEach((button) => button.addEventListener('click', () => this.go(this.previousStep()), { signal }));
    this.querySelector('[data-restart]')?.addEventListener('click', () => this.restart(), { signal });
  }

  /* ----- steps ----- */
  nextStep() {
    return this.current === 3 && this.data.product !== 'hoodie' ? 5 : this.current + 1;
  }

  previousStep() {
    return this.current === 5 && this.data.product !== 'hoodie' ? 3 : this.current - 1;
  }

  go(step, focus = true) {
    this.current = step;
    this.steps.forEach((el) => { el.hidden = Number(el.dataset.step) !== step; });
    const hoodie = this.data.product === 'hoodie';
    const shown = Math.min(step, 5);
    this.dots.forEach((dot) => {
      const n = Number(dot.dataset.dot);
      dot.classList.toggle('is-active', n === 4 && !hoodie ? shown >= 5 : n <= shown);
    });
    if (this.stepLabel && step < 6) {
      const total = hoodie ? 5 : 4;
      const index = !hoodie && step === 5 ? 4 : step;
      this.stepLabel.textContent = (this.dataset.stepText || '').replace('[current]', index).replace('[total]', total);
    }
    // Focus straight away (inside the tap that opened the step) so phones show the keyboard at once.
    if (!focus) return;
    const target = this.steps.find((el) => !el.hidden);
    const input = target?.querySelector('input[type="number"]');
    (input || target?.querySelector('[data-result-text], .size-finder__question'))?.focus({ preventScroll: true });
  }

  restart() {
    this.data = this.emptyData();
    this.heightInput.value = '';
    this.weightInput.value = '';
    this.querySelectorAll('[data-continue]').forEach((button) => { if (button.closest('[data-step]').querySelector('input[type="number"]')) button.disabled = true; });
    this.shoulderInputs.forEach((input) => { input.checked = input.value === 'normal'; });
    this.fitInputs.forEach((input) => { input.checked = input.value === 'regular'; });
    this.clear();
    this.go(1);
  }

  /* ----- size maths ----- */
  calculateSize() {
    const { height, weight, shoulder, fit, product } = this.data;
    let index;
    if (height < 155) index = 0;
    else if (height <= 165) index = weight <= 60 ? 0 : 1;
    else if (height <= 173) index = weight <= 70 ? 1 : 2;
    else if (height <= 180) index = weight <= 80 ? 2 : 3;
    else if (height <= 187) index = weight <= 90 ? 3 : 4;
    else if (height <= 195) index = weight <= 100 ? 4 : 5;
    else index = 5;

    if (product === 'hoodie') {
      if (shoulder === 'narrow') index -= 1;
      if (shoulder === 'wide') index += 1;
    }
    if (fit === 'slim') index -= 1;
    if (fit === 'oversized') index += 1;
    return SIZES[Math.max(0, Math.min(SIZES.length - 1, index))];
  }

  async showResult(focus = true) {
    const { product, height, weight, shoulder, fit } = this.data;
    const size = this.calculateSize();
    this.data.size = size;
    this.save();

    this.querySelector('[data-result-size]').textContent = size;
    const word = (key) => this.dataset[`word${key[0].toUpperCase()}${key.slice(1)}`] || key;
    const template = product === 'hoodie' ? this.dataset.textHoodie : this.dataset.textOther;
    const text = (template || '')
      .replace('[height]', height).replace('[weight]', weight).replace('[size]', size)
      .replace('[shoulder]', word(shoulder)).replace('[fit]', word(fit));
    this.querySelector('[data-result-text]').textContent = text;

    const url = this.dataset[`${product}Url`];
    this.querySelector('[data-result-cta]').href = url || this.dataset.fallbackUrl || '/collections/all';
    this.go(6, focus);
    await this.loadProducts(url);
  }

  async loadProducts(collectionUrl) {
    const holder = this.querySelector('[data-result-products]');
    holder.replaceChildren();
    if (!collectionUrl) return;
    this.productsRequest?.abort();
    this.productsRequest = new AbortController();
    try {
      const response = await fetch(`${collectionUrl.replace(/\/$/, '')}/products.json?limit=4`, { signal: this.productsRequest.signal });
      if (!response.ok) return;
      const { products } = await response.json();
      if (!products?.length) return;

      const root = this.dataset.root || '/';
      const grid = make('div', 'size-finder__grid');
      products.forEach((product) => {
        const card = make('a', 'size-finder__product');
        card.href = `${root}products/${product.handle}`;
        const image = product.images && product.images[0];
        if (image) {
          const src = new URL(image.src, location.href);
          src.searchParams.set('width', '300');
          const img = make('img', 'size-finder__product-image');
          img.src = src.toString();
          img.alt = image.alt || product.title;
          img.loading = 'lazy';
          img.width = 300;
          img.height = 300;
          card.append(img);
        }
        card.append(make('div', 'size-finder__product-title', product.title));
        const cents = Math.round(parseFloat(product.variants?.[0]?.price) * 100);
        if (Number.isFinite(cents)) card.append(make('div', 'size-finder__product-price', window.Theme ? window.Theme.formatMoney(cents) : (cents / 100).toFixed(2)));
        grid.append(card);
      });
      holder.append(make('h3', 'size-finder__products-title', this.dataset.productsTitle || ''), grid);
    } catch (error) {
      if (error.name !== 'AbortError') holder.replaceChildren();
    }
  }

  /* ----- storage ----- */
  save() {
    try { localStorage.setItem(this.dataset.storageKey, JSON.stringify(this.data)); } catch (e) { /* storage unavailable */ }
  }

  clear() {
    try { localStorage.removeItem(this.dataset.storageKey); } catch (e) { /* storage unavailable */ }
  }

  restore() {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(this.dataset.storageKey) || 'null'); } catch (e) { /* ignore */ }
    if (!saved || !saved.size || !saved.product || !saved.height || !saved.weight) return;
    this.data = { ...this.emptyData(), ...saved };
    this.heightInput.value = this.data.height;
    this.weightInput.value = this.data.weight;
    this.querySelectorAll('[data-continue]').forEach((button) => { button.disabled = false; });
    this.shoulderInputs.forEach((input) => { input.checked = input.value === this.data.shoulder; });
    this.fitInputs.forEach((input) => { input.checked = input.value === this.data.fit; });
    this.showResult(false);
  }
}

if (!customElements.get('size-finder')) customElements.define('size-finder', SizeFinder);
